/**
 * Employee Ledgers API client — loans / canteen txns / colony bills.
 * F&F auto-pulls outstanding from these via separation_dues_fetcher.
 */
import apiClient from '@/shared/api/client'

export const LOAN_TYPES = [
  'SALARY_ADVANCE', 'FESTIVAL_ADVANCE', 'EMERGENCY_LOAN',
  'EDUCATION_LOAN', 'MEDICAL_LOAN',
  // Phase 3.5 — bond-style loans (auto-waive on completion)
  'RECRUITMENT_BOND', 'TRAINING_BOND',
  'OTHER',
] as const
export type LoanType = (typeof LOAN_TYPES)[number]

// Loan types that follow the bond all-or-nothing waiver rule.
export const BOND_LOAN_TYPES: ReadonlySet<LoanType> = new Set([
  'RECRUITMENT_BOND', 'TRAINING_BOND',
])
export const isBondLoanType = (t: string | undefined): boolean =>
  !!t && BOND_LOAN_TYPES.has(t as LoanType)

export const LOAN_STATUSES = ['ACTIVE', 'FULLY_PAID', 'WAIVED', 'CANCELLED'] as const
export type LoanStatus = (typeof LOAN_STATUSES)[number]

export const CANTEEN_TRANSACTION_TYPES = [
  'COUPON_ISSUED', 'MEAL_CHARGE', 'PAYMENT', 'REFUND', 'ADJUSTMENT',
] as const
export type CanteenTransactionType = (typeof CANTEEN_TRANSACTION_TYPES)[number]

export const COLONY_BILL_STATUSES = ['OPEN', 'PARTIAL', 'PAID', 'WAIVED', 'WRITTEN_OFF'] as const
export type ColonyBillStatus = (typeof COLONY_BILL_STATUSES)[number]

export interface EmployeeLoan {
  id: string
  loan_no: string
  employee_id: string
  employee_code: string
  loan_type: LoanType
  purpose: string | null
  principal_amount: number
  interest_rate_pct: number
  emi_count: number
  emi_amount: number
  sanction_date: string
  first_recovery_date: string
  /** Phase 3.5 — only present for bond-type loans */
  bond_period_months: number | null
  bond_start_date: string | null
  outstanding_principal: number
  status: LoanStatus
  remarks: string | null
  created_at: string | null
}

export interface CanteenTransaction {
  id: string
  employee_id: string
  employee_code: string
  canteen_id: string | null
  transaction_date: string
  transaction_type: CanteenTransactionType
  coupon_count: number | null
  rate_per_coupon: number | null
  amount: number
  balance_after: number
  payment_reference: string | null
  remarks: string | null
  created_at: string | null
}

export interface ColonyBill {
  id: string
  bill_no: string
  employee_id: string
  employee_code: string
  room_id: string | null
  colony_id: string | null
  period_year: number
  period_month: number
  rent_amount: number
  electricity_amount: number
  water_amount: number
  maintenance_amount: number
  damage_recovery: number
  other_charges: number
  arrears_brought_forward: number
  total_billed: number
  paid_amount: number
  outstanding_amount: number
  status: ColonyBillStatus
  paid_at: string | null
  payment_reference: string | null
  created_at: string | null
}

const BASE = '/hr/ledgers'

export const ledgerApi = {
  // Loans
  listLoans: async (params: { employee_id?: string; status?: LoanStatus } = {}) =>
    (await apiClient.get<EmployeeLoan[]>(`${BASE}/loans`, { params })).data,
  createLoan: async (body: {
    employee_id: string
    loan_type: LoanType
    principal_amount: number
    emi_count: number
    sanction_date: string
    first_recovery_date: string
    interest_rate_pct?: number
    purpose?: string
    remarks?: string
    bond_period_months?: number
    bond_start_date?: string
  }) => (await apiClient.post<EmployeeLoan>(`${BASE}/loans`, body)).data,
  recordLoanRecovery: async (loan_id: string, body: {
    amount: number
    recovered_date: string
    instalment_no?: number
    remarks?: string
  }) => (await apiClient.post(`${BASE}/loans/${loan_id}/record-recovery`, body)).data,

  // Canteen
  listCanteen: async (params: {
    employee_id?: string
    date_from?: string
    date_to?: string
  } = {}) =>
    (await apiClient.get<CanteenTransaction[]>(`${BASE}/canteen-transactions`, { params })).data,
  createCanteenTxn: async (body: {
    employee_id: string
    transaction_date: string
    transaction_type: CanteenTransactionType
    amount: number
    coupon_count?: number
    rate_per_coupon?: number
    payment_reference?: string
    remarks?: string
  }) => (await apiClient.post<CanteenTransaction>(`${BASE}/canteen-transactions`, body)).data,

  // Colony
  listBills: async (params: { employee_id?: string; status?: ColonyBillStatus } = {}) =>
    (await apiClient.get<ColonyBill[]>(`${BASE}/colony-bills`, { params })).data,
  createBill: async (body: {
    employee_id: string
    period_year: number
    period_month: number
    rent_amount?: number
    electricity_amount?: number
    water_amount?: number
    maintenance_amount?: number
    damage_recovery?: number
    other_charges?: number
    arrears_brought_forward?: number
    remarks?: string
  }) => (await apiClient.post<ColonyBill>(`${BASE}/colony-bills`, body)).data,
  recordBillPayment: async (bill_id: string, body: {
    amount: number
    payment_reference: string
  }) => (await apiClient.post<ColonyBill>(`${BASE}/colony-bills/${bill_id}/record-payment`, body)).data,
}

export const LOAN_STATUS_COLOR: Record<LoanStatus, string> = {
  ACTIVE: 'processing',
  FULLY_PAID: 'success',
  WAIVED: 'default',
  CANCELLED: 'default',
}

export const BILL_STATUS_COLOR: Record<ColonyBillStatus, string> = {
  OPEN: 'gold',
  PARTIAL: 'orange',
  PAID: 'success',
  WAIVED: 'default',
  WRITTEN_OFF: 'red',
}
